import Decimal from 'decimal.js-light';
import { Building2, Pencil, Settings2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { HoldingDto, PortfolioSummaryDto } from '../../shared/types';
import { displayMoney, formatCurrency, formatNumber, type DisplayCurrency } from '../format';
import { sortHoldingGroupsByName } from '../holding-groups';
import { aggregateHoldings, holdingDisplayValues, holdingPresentationGroup, sortHoldingsByReturn } from '../holdings';
import { StatusDot } from './Icons';
import { AssetAvatar } from './AssetAvatar';

const groups = [
  { key: 'TH_EQUITY', label: 'หุ้นไทย', badge: 'TH', alwaysShow: true, style: 'th_equity', mode: 'asset' },
  { key: 'US_EQUITY', label: 'หุ้นสหรัฐ', badge: 'US', alwaysShow: true, style: 'us_equity', mode: 'asset' },
  { key: 'FOREIGN_CURRENCY', label: 'เงินต่างประเทศ', badge: 'FX', alwaysShow: true, style: 'fiat', mode: 'foreignCurrency' },
  { key: 'THB_CASH', label: 'เงินฝากธนาคาร', badge: '฿', alwaysShow: false, style: 'fiat', mode: 'bankDeposit' },
  { key: 'GOLD', label: 'ทองคำ', badge: 'AU', alwaysShow: false, style: 'gold', mode: 'asset' },
  { key: 'CRYPTO', label: 'Crypto', badge: '₿', alwaysShow: false, style: 'crypto', mode: 'asset' },
] as const;
const quoteLabels = { FRESH: 'ล่าสุด', MANUAL: 'กรอกเอง', STALE: 'ราคาเก่า', MISSING: 'ไม่มีราคา', FAILED: 'ราคาไม่พร้อม' };
const totalOf = (holdings: HoldingDto[]) => holdings.reduce((sum, holding) => sum.add(holding.marketValueUsd), new Decimal(0));
const weight = (value: string | Decimal, total: string | Decimal) => new Decimal(total).isZero() ? '0.00' : new Decimal(value).div(total).mul(100).toFixed(2);

export function HoldingsSection({ summary, currency, onEditPrice, emptyAction }: {
  summary: PortfolioSummaryDto;
  currency: DisplayCurrency;
  onEditPrice: (holding: HoldingDto) => void;
  emptyAction?: ReactNode;
}) {
  const combined = aggregateHoldings(summary.holdings, summary.stats.marketValueUsd);
  return <section className="holdings-section" id="holdings" aria-labelledby="holdings-title">
    <div className="holdings-heading">
      <div><span className="panel-kicker">HOLDINGS</span><h2 id="holdings-title">สินทรัพย์ที่ถืออยู่</h2></div>
      <Link to="/transactions" className="text-link">ดูรายการทั้งหมด →</Link>
    </div>
    {!summary.holdings.length && emptyAction}
    <div className="holdings-groups">
      {groups.map((group) => {
        // Bank deposits stay separated by account; other assets are combined
        // across accounts before presentation.
        const source = group.key === 'THB_CASH' ? summary.holdings : combined;
        const holdings = source.filter((holding) => holdingPresentationGroup(holding.instrument) === group.key);
        if (!group.alwaysShow && !holdings.length) return null;
        const isStock = group.key === 'TH_EQUITY' || group.key === 'US_EQUITY';
        const groupCurrency: DisplayCurrency = group.key === 'TH_EQUITY' || group.key === 'FOREIGN_CURRENCY' || group.key === 'THB_CASH'
          ? 'THB' : group.key === 'US_EQUITY' ? 'USD' : currency;
        const value = totalOf(holdings);
        const subgroups = sortHoldingGroupsByName(summary.holdingGroups || [])
          .filter((subgroup) => subgroup.marketGroup === group.key);
        const assigned = new Set(subgroups.flatMap((subgroup) => subgroup.instrumentIds));
        const sections = subgroups.map((subgroup) => ({
          id: subgroup.id, name: subgroup.name, holdings: holdings.filter((holding) => subgroup.instrumentIds.includes(holding.instrumentId)),
        }));
        const unassigned = holdings.filter((holding) => !assigned.has(holding.instrumentId));
        if (unassigned.length || !sections.length) sections.push({ id: '', name: isStock ? 'ยังไม่จัดกลุ่ม' : '', holdings: unassigned });

        return <section className={`panel holdings-panel holdings-${group.key.toLowerCase()}`} key={group.key} aria-labelledby={`holdings-${group.key}`}>
          <div className="holding-group-head">
            <div className="holding-group-title">
              <span className={`holding-group-badge asset-${group.style}`} aria-hidden="true">{group.badge}</span>
              <div><h3 id={`holdings-${group.key}`}>{group.label} <span className="holding-count">{holdings.length}</span></h3><p>แสดงเป็น {groupCurrency}</p></div>
            </div>
            <div className="holding-group-total"><strong>{displayMoney(value.toFixed(), groupCurrency, summary.usdThb, 2)}</strong><span>{weight(value, summary.stats.marketValueUsd)}% พอร์ต</span></div>
          </div>
          {isStock && <div className="holding-group-actions"><Link className="text-link" to="/settings#holding-groups"><Settings2 size={12} /> จัดการกลุ่มย่อย</Link></div>}
          {sections.map((section) => <div className="holding-subgroup" key={section.id || 'unassigned'}>
            {isStock && <div className="holding-subgroup-head"><h4>{section.name}</h4><span>{section.holdings.length} หุ้น · {displayMoney(totalOf(section.holdings).toFixed(), groupCurrency, summary.usdThb, 2)} · {weight(totalOf(section.holdings), value)}% {group.label}</span></div>}
            {section.holdings.length ? <HoldingTable holdings={section.holdings} currency={groupCurrency} usdThb={summary.usdThb}
              groupName={section.name || group.label} subgroupName={section.id ? section.name : null} isStock={isStock} mode={group.mode} onEditPrice={onEditPrice} />
              : <div className="holding-group-empty">{isStock ? 'ยังไม่มีหุ้นในกลุ่มนี้' : 'ยังไม่มีสินทรัพย์ที่บันทึกไว้'}</div>}
          </div>)}
        </section>;
      })}
    </div>
    <div className="holdings-explanation">
      <p>เรียงตาม % กำไร/ขาดทุนจากมากไปน้อยภายในแต่ละกลุ่มย่อย · หุ้นตัวเดียวกันรวมทุกบัญชีเป็นหนึ่งรายการ</p>
      <p>สัดส่วนใต้ชื่อสินทรัพย์เทียบกับมูลค่าพอร์ตทั้งหมดและมูลค่ากลุ่มย่อย · ต้นทุนรวมประกอบด้วยมูลค่าสินทรัพย์ ค่าคอมมิชชัน และภาษี หักส่วนลดของหน่วยที่ยังถืออยู่</p>
      <p>ราคาซื้อเฉลี่ยและอัตราแลกเปลี่ยนเฉลี่ยคำนวณแบบถ่วงน้ำหนัก ไม่รวมค่าธรรมเนียมและภาษี · กำไร/ขาดทุน = มูลค่าปัจจุบัน − ต้นทุนรวม</p>
    </div>
  </section>;
}

function HoldingTable({ holdings, currency, usdThb, groupName, subgroupName, isStock, mode, onEditPrice }: {
  holdings: HoldingDto[]; currency: DisplayCurrency; usdThb: string; groupName: string; subgroupName: string | null;
  isStock: boolean; mode: 'asset' | 'foreignCurrency' | 'bankDeposit'; onEditPrice: (holding: HoldingDto) => void;
}) {
  const isBankDeposit = mode === 'bankDeposit';
  const isForeignCurrency = mode === 'foreignCurrency';
  const scrollRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const measure = () => setOverflowing(container.scrollWidth > container.clientWidth + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    if (container.firstElementChild) observer.observe(container.firstElementChild);
    measure();
    return () => observer.disconnect();
  }, []);
  const groupValue = totalOf(holdings);
  const mappedRows = holdings.map((holding) => {
    const values = holdingDisplayValues(holding, currency, usdThb);
    return { holding, values, currentPrice: values.currentPrice, unrealizedPlPct: values.unrealizedPlPct };
  });
  const rows = isBankDeposit
    ? mappedRows.sort((a, b) => `${a.holding.accountInstitution || ''} ${a.holding.accountName}`.localeCompare(
      `${b.holding.accountInstitution || ''} ${b.holding.accountName}`, 'th',
    ))
    : sortHoldingsByReturn(mappedRows);
  const columns = isBankDeposit ? ['ธนาคาร / บัญชี', 'ยอดเงินฝาก', 'อัตราดอกเบี้ย']
    : isForeignCurrency
      ? ['สกุลเงิน', 'จำนวนเงิน', 'ต้นทุนรวม', 'อัตราแลกเปลี่ยนเฉลี่ย', 'อัตราแลกเปลี่ยนล่าสุด', 'มูลค่าปัจจุบัน', 'กำไร/ขาดทุน']
      : ['สินทรัพย์', 'จำนวน', 'ต้นทุนรวม', 'ราคาซื้อเฉลี่ย', 'ราคาล่าสุด', 'มูลค่าปัจจุบัน', 'กำไร/ขาดทุน'];
  return <>
    {overflowing && <p className="holdings-scroll-hint">เลื่อนตารางเพื่อดูทุกคอลัมน์ →</p>}
    <div className="holdings-table-scroll" ref={scrollRef} data-overflow={overflowing} tabIndex={overflowing ? 0 : undefined}
      role={overflowing ? 'region' : undefined} aria-label={overflowing ? `ตาราง${groupName} เลื่อนแนวนอนได้` : undefined}>
      <table className={`holdings-table${isBankDeposit ? ' holdings-cash-table' : ''}`}>
      <caption className="sr-only">สินทรัพย์ในกลุ่ม{groupName} แสดงเป็น {currency}</caption>
      <thead><tr>{columns.map((label) => <th scope="col" key={label} aria-sort={label === 'กำไร/ขาดทุน' ? 'descending' : undefined}>{label}{label === 'กำไร/ขาดทุน' && <span aria-hidden="true"> ↓</span>}</th>)}</tr></thead>
      <tbody>{rows.map(({ holding, values }) => <tr key={`${holding.accountId}:${holding.instrumentId}`}>
        <th scope="row" className="holding-asset-cell"><div className="holding-asset">
          {isBankDeposit ? <span className="asset-avatar asset-fiat" aria-hidden="true"><Building2 size={16} /></span> : <AssetAvatar instrument={holding.instrument} />}
          {isBankDeposit
            ? <span><strong>{holding.accountInstitution || holding.accountName}</strong><small>{holding.accountInstitution ? holding.accountName : 'บัญชีเงินฝาก'} · {holding.instrument.symbol}</small></span>
            : <span><strong>{holding.instrument.symbol}</strong><small>{isStock ? `${holding.allocationPct.toFixed(2)}% พอร์ต` : `${weight(holding.marketValueUsd, groupValue)}% ${groupName}`}</small>
              {subgroupName && <small>{weight(holding.marketValueUsd, groupValue)}% {subgroupName}</small>}
            </span>}
        </div></th>
        {isBankDeposit
          ? <><td data-label="ยอดเงินฝาก">{formatCurrency(holding.quantity, 'THB')}</td><td data-label="อัตราดอกเบี้ย">{holding.accountInterestRatePct == null ? '—' : <span>{formatNumber(holding.accountInterestRatePct, 2)}%<small>ต่อปี</small></span>}</td></>
          : <td data-label={isForeignCurrency ? 'จำนวนเงิน' : 'จำนวน'}>{formatNumber(holding.quantity)}<small>{isForeignCurrency ? holding.instrument.symbol : holding.instrument.unit}</small></td>}
        {!isBankDeposit && <>
          <td data-label="ต้นทุนรวม" className="holding-cost-cell">
            {values.costBasisAvailable ? <details className="holding-cost-details">
              <summary aria-label={`ดูรายละเอียดต้นทุน ${holding.instrument.symbol}`}>
                <span>{formatCurrency(values.bookCost, currency)}</span><small>ดูรายละเอียด</small>
              </summary>
              <div className="holding-cost-breakdown">
                <span><span>{isForeignCurrency ? 'เงินต้นที่แลก' : isStock ? 'มูลค่าหุ้น' : holding.instrument.kind === 'GOLD' ? 'มูลค่าทองคำ' : 'มูลค่าสินทรัพย์'}</span><strong>{formatCurrency(values.grossCost, currency, 4)}</strong></span>
                <span><span>ค่าคอมมิชชัน</span><strong>{formatCurrency(values.feeCost, currency, 4)}</strong></span>
                <span><span>ภาษี</span><strong>{formatCurrency(values.taxCost, currency, 4)}</strong></span>
                <span><span>ส่วนลด</span><strong>−{formatCurrency(values.discountCost, currency, 4)}</strong></span>
                <span className="holding-cost-total"><span>ต้นทุนรวม</span><strong>{formatCurrency(values.bookCost, currency, 4)}</strong></span>
              </div>
            </details> : '—'}
          </td>
          <td data-label={isForeignCurrency ? 'อัตราแลกเปลี่ยนเฉลี่ย' : 'ราคาซื้อเฉลี่ย'} title={`${isForeignCurrency ? 'อัตราแลกเปลี่ยน' : 'ราคา'}ถ่วงน้ำหนัก ไม่รวมค่าคอมมิชชันและภาษี`}>{values.costBasisAvailable ? formatCurrency(values.averagePurchasePrice, currency, 4) : '—'}</td>
          <td data-label={isForeignCurrency ? 'อัตราแลกเปลี่ยนล่าสุด' : 'ราคาล่าสุด'}>{values.currentPrice ? formatCurrency(values.currentPrice, currency, 4) : '—'}
            <small className="holding-price-status" title={holding.quoteSource || undefined}><StatusDot status={holding.quoteStatus} />{holding.estimated ? 'ราคาประเมิน' : quoteLabels[holding.quoteStatus]}</small>
            {!isForeignCurrency && <button className="price-edit-button" onClick={() => onEditPrice(holding)} aria-label={`กรอกราคา ${holding.instrument.symbol}`}><Pencil size={11} /> แก้ราคา</button>}
          </td>
        </>}
        {!isBankDeposit && <td data-label="มูลค่าปัจจุบัน" className="holding-market-value">{values.marketValue !== null ? formatCurrency(values.marketValue, currency) : '—'}</td>}
        {!isBankDeposit && <td data-label="กำไร/ขาดทุน" className={values.unrealizedPl !== null ? Number(values.unrealizedPl) >= 0 ? 'gain' : 'loss' : 'muted'}>
          {values.costBasisAvailable && values.unrealizedPl !== null ? <span className="holding-return">{values.unrealizedPlPct === null ? '—' : `${Number(values.unrealizedPlPct).toFixed(2)}%`}<span>({formatCurrency(values.unrealizedPl, currency)})</span></span> : '—'}
        </td>}
      </tr>)}</tbody>
    </table></div>
  </>;
}
